import React, { useState, useEffect } from 'react';

const CustomFields = ({ fields, onChange }) => {
  const [localFields, setLocalFields] = useState(fields || []);

  useEffect(() => {
    setLocalFields(fields || []);
  }, [fields]);

  const addField = () => {
    const newField = {
      title: '',
      title_ar: '',
      name: '',
      type: 'input',
      options: [{ en: '', ar: '' }],
      is_multiple: false,
      required: false
    };
    const updatedFields = [...localFields, newField];
    setLocalFields(updatedFields);
    onChange(updatedFields);
  };

  const removeField = (index) => {
    const updatedFields = localFields.filter((_, i) => i !== index);
    setLocalFields(updatedFields);
    onChange(updatedFields);
  };

  const updateField = (index, field, value) => {
    const updatedFields = [...localFields];
    updatedFields[index] = { ...field, ...value };
    setLocalFields(updatedFields);
    onChange(updatedFields);
  };

  const addOption = (fieldIndex) => {
    const updatedFields = [...localFields];
    if (!updatedFields[fieldIndex].options) {
      updatedFields[fieldIndex].options = [];
    }
    updatedFields[fieldIndex].options.push({ en: '', ar: '' });
    setLocalFields(updatedFields);
    onChange(updatedFields);
  };

  const removeOption = (fieldIndex, optionIndex) => {
    const updatedFields = [...localFields];
    updatedFields[fieldIndex].options.splice(optionIndex, 1);
    if (updatedFields[fieldIndex].options.length === 0) {
      updatedFields[fieldIndex].options = [{ en: '', ar: '' }];
    }
    setLocalFields(updatedFields);
    onChange(updatedFields);
  };

  const updateOption = (fieldIndex, optionIndex, language, value) => {
    const updatedFields = [...localFields];
    const currentOptions = [...updatedFields[fieldIndex].options];
    currentOptions[optionIndex] = {
      ...currentOptions[optionIndex],
      [language]: value
    };
    updatedFields[fieldIndex].options = currentOptions;
    setLocalFields(updatedFields);
    onChange(updatedFields);
  };

  const generateFieldName = (title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_');
  };

  return (
    <div className="custom-fields-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 className="fw-semibold mb-1">Custom Fields</h6>
          <p className="text-muted small mb-0">Add custom input fields for your items</p>
        </div>
        <button
          type="button"
          className="btn btn-base btn-sm"
          onClick={addField}
        >
          <i className="bi bi-plus-circle me-1"></i> Add Field
        </button>
      </div>

      {localFields.map((field, fieldIndex) => (
        <div key={fieldIndex} className="border bg-white p-3 my-3 rounded shadow-sm">
          <div className="card-body">
            {/* Field Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">Field #{fieldIndex + 1}</h6>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => removeField(fieldIndex)}
              >
                <i className="bi bi-trash"></i> Remove
              </button>
            </div>

            {/* Basic Field Info */}
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <label className="form-label">
                  English Title <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={field.title || ''}
                  onChange={(e) => {
                    const title = e.target.value;
                    const name = generateFieldName(title);
                    updateField(fieldIndex, field, { title, name });
                  }}
                  placeholder="e.g., How Many Hours?"
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Arabic Title <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={field.title_ar || ''}
                  onChange={(e) => updateField(fieldIndex, field, { title_ar: e.target.value })}
                  placeholder="مثال: كم عدد الساعات؟"
                  dir="rtl"
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Field Name (Auto-generated)
                </label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={field.name || ''}
                  onChange={(e) => updateField(fieldIndex, field, { name: e.target.value })}
                  placeholder="e.g., no_hours"
                  readOnly
                />
                <small className="text-muted">This will be used in the database</small>
              </div>
            </div>

            {/* Field Type and Settings */}
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <label className="form-label">Field Type</label>
                <select
                  className="form-select"
                  value={field.type || 'input'}
                  onChange={(e) => {
                    const selectedType = e.target.value;
                    const updates = { type: selectedType };

                    if (selectedType === 'options') {
                      updates.options = field.options && field.options.length 
                        ? field.options 
                        : [{ en: '', ar: '' }];
                    } else {
                      updates.options = [];
                      updates.is_multiple = false;
                    }

                    updateField(fieldIndex, field, updates);
                  }}
                >
                  <option value="input">Text Input</option>
                  <option value="options">Dropdown Options</option>
                </select>
              </div>

              <div className="col-md-3">
                <div className="form-check form-switch mt-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`required-${fieldIndex}`}
                    checked={field.required || false}
                    onChange={(e) =>
                      updateField(fieldIndex, field, { required: e.target.checked })
                    }
                  />
                  <label className="form-check-label" htmlFor={`required-${fieldIndex}`}>
                    Required Field
                  </label>
                </div>
              </div>

              {field.type === 'options' && (
                <div className="col-md-3">
                  <div className="form-check form-switch mt-4">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`is-multiple-${fieldIndex}`}
                      checked={field.is_multiple || false}
                      onChange={(e) =>
                        updateField(fieldIndex, field, { is_multiple: e.target.checked })
                      }
                    />
                    <label className="form-check-label" htmlFor={`is-multiple-${fieldIndex}`}>
                      Allow Multiple Selections
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Options Section - Only for 'options' type */}
            {field.type === 'options' && (
              <div className="border rounded p-3 bg-light mt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <label className="form-label fw-semibold mb-0">Options</label>
                    <p className="text-muted small mb-0">
                      Add options for selection (English and Arabic)
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-dark"
                    onClick={() => addOption(fieldIndex)}
                  >
                    <i className="bi bi-plus-circle me-1"></i> Add Option
                  </button>
                </div>

                <div className="row g-3">
                  {(field.options || []).map((option, optionIndex) => (
                    <div key={optionIndex} className="col-md-6">
                      <div className="border p-3 rounded bg-white">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="badge bg-secondary text-gray-600">Option #{optionIndex + 1}</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeOption(fieldIndex, optionIndex)}
                            disabled={field.options.length <= 1}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                        
                        <div className="row g-2">
                          <div className="col-md-6">
                            <label className="form-label small">English</label>
                            <input
                              type="text"
                              className="form-control"
                              value={option.en || ''}
                              onChange={(e) => updateOption(fieldIndex, optionIndex, 'en', e.target.value)}
                              placeholder="e.g., Small"
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small">Arabic</label>
                            <input
                              type="text"
                              className="form-control"
                              value={option.ar || ''}
                              onChange={(e) => updateOption(fieldIndex, optionIndex, 'ar', e.target.value)}
                              placeholder="مثال: صغير"
                              dir="rtl"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                
              </div>
            )}
          </div>
        </div>
      ))}

      {localFields.length === 0 && (
        <div className="text-center py-5 border rounded bg-light">
          <i className="bi bi-input-cursor-text display-5 text-muted mb-3"></i>
          <h6 className="text-muted">No custom fields added yet</h6>
          <p className="text-muted small mb-3">Click the button below to add your first custom field</p>
          <button
            type="button"
            className="btn btn-base"
            onClick={addField}
          >
            <i className="bi bi-plus-circle text-white me-2"></i> Add First Field
          </button>
        </div>
      )}

      <style jsx>{`
        .custom-fields-container {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        input[dir="rtl"] {
          font-family: 'Segoe UI', 'Arial', sans-serif;
        }
        .bg-light {
          background-color: #f8f9fa !important;
        }
      `}</style>
    </div>
  );
};

export default CustomFields;